import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (password.length < 6) return setErr("Password must be at least 6 characters.");
        if (password !== confirm) return setErr("Passwords do not match.");
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            setDone(true);
            toast.success("Password reset");
        } catch (e2) {
            setErr(apiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-pale px-4" data-testid="reset-password-page">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-navy/10">
                    <Lock className="h-5 w-5 text-navy" />
                </div>
                {done ? (
                    <div className="text-center">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-teal/10"><CheckCircle2 className="h-7 w-7 text-teal" /></div>
                        <h1 className="font-heading text-2xl font-bold text-navy">Password reset</h1>
                        <p className="mt-2 text-sm text-slate-600">Your password has been updated. You can now sign in.</p>
                        <Button onClick={() => navigate("/")} data-testid="reset-done-btn" className="mt-6 w-full bg-navy text-white hover:bg-navy-light">Back to sign in</Button>
                    </div>
                ) : !token ? (
                    <div className="text-center">
                        <h1 className="font-heading text-2xl font-bold text-navy">Invalid link</h1>
                        <p className="mt-2 text-sm text-slate-600">This reset link is missing its token. Please request a new one from the sign-in screen.</p>
                        <Button onClick={() => navigate("/")} className="mt-6 w-full bg-navy text-white hover:bg-navy-light">Back to site</Button>
                    </div>
                ) : (
                    <>
                        <h1 className="text-center font-heading text-2xl font-bold text-navy">Choose a new password</h1>
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="reset-password">New password</Label>
                                <Input id="reset-password" data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="reset-confirm">Confirm password</Label>
                                <Input id="reset-confirm" data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="••••••••" />
                            </div>
                            {err && <p className="text-sm text-red-500" data-testid="reset-error">{err}</p>}
                            <Button type="submit" disabled={busy} data-testid="reset-submit-btn" className="h-11 w-full bg-navy font-semibold text-white hover:bg-navy-light">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
                            </Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
